import {Component, ElementRef, ViewChild} from '@angular/core';
import {RestApiService} from '../../../services/api/rest-api.service';
import {ToastService} from '../../../services/toast.service';
import {Button} from 'primeng/button';
import {RestApiError} from '../../../services/api/rest-api-error';
import {downloadAttachment} from '../../../utils/download-utils';
import {Router} from '@angular/router';
import {Routing} from '../../../models/routing/routing';

type ImportCataloguePayload = {
  errors?: unknown[];
};

type ImportCatalogueError = {
  row?: number | string;
  column?: string;
  message: string;
};

@Component({
  selector: 'app-catalogue-import',
  standalone: true,
  imports: [
    Button
  ],
  templateUrl: './catalogue-import.component.html',
  styleUrl: './catalogue-import.component.scss'
})
export class CatalogueImportComponent {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  selectedFile?: File;
  loading = false;
  downloadingCatalogue = false;
  dragging = false;
  importErrors: ImportCatalogueError[] = [];

  readonly acceptedExtensions = ['.xlsx'];

  constructor(private readonly api: RestApiService,
              private readonly toastService: ToastService,
              private readonly router: Router) {
  }

  get canUpload() {
    return !!this.selectedFile && !this.loading;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.setFile(input.files?.[0]);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
    this.setFile(event.dataTransfer?.files?.[0]);
  }

  clearFile() {
    this.selectedFile = undefined;
    this.importErrors = [];
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  async upload() {
    if (!this.selectedFile) {
      this.toastService.warning('File mancante', 'Seleziona un file Excel da caricare.');
      return;
    }

    try {
      this.loading = true;
      this.importErrors = [];

      const response = await this.api.movies.importCatalogue({
        value: this.selectedFile
      });

      const errors = this.normalizeImportErrors(response.data);
      if (errors.length) {
        this.showImportErrors(errors);
        return;
      }

      const migrationId = response.data?.migrationId;
      if (!migrationId) {
        this.toastService.error('Import completato', 'MigrationId non presente nella risposta.');
        return;
      }

      this.toastService.success('Catalogo aggiornato', 'Il file Excel è stato inviato correttamente.');
      this.clearFile();
      await this.router.navigateByUrl(Routing.movies.catalogueImportRecap(migrationId, true), {replaceUrl: true});
    } catch (error) {
      const errors = this.extractImportErrors(error);
      if (errors.length) {
        this.showImportErrors(errors);
        return;
      }

      throw error;
    } finally {
      this.loading = false;
    }
  }

  async downloadCatalogueExcel() {
    try {
      this.downloadingCatalogue = true;
      await downloadAttachment(this.api.catalogue.downloadExcel(), 'Catalogo_Vision_ADMIN.xlsx');
    } finally {
      this.downloadingCatalogue = false;
    }
  }

  private setFile(file?: File) {
    if (!file) {
      return;
    }

    if (!this.isExcelFile(file)) {
      this.toastService.error('Formato non valido', 'Carica un file .xls o .xlsx.');
      this.clearFile();
      return;
    }

    this.selectedFile = file;
    this.importErrors = [];
  }

  private isExcelFile(file: File) {
    const fileName = file.name.toLowerCase();
    return this.acceptedExtensions.some(extension => fileName.endsWith(extension));
  }

  private showImportErrors(errors: ImportCatalogueError[]) {
    this.importErrors = errors;
    this.toastService.error('Import non completato', 'Correggi gli errori indicati e riprova.');
  }

  private extractImportErrors(error: unknown) {
    if (error instanceof RestApiError) {
      return this.normalizeImportErrors(error.response);
    }

    const responseData = (error as { response?: { data?: unknown }, error?: unknown } | null)?.response?.data;
    const errorData = (error as { error?: unknown } | null)?.error;

    return this.normalizeImportErrors(responseData ?? errorData ?? error);
  }

  private normalizeImportErrors(payload: unknown) {
    const errors = (payload as ImportCataloguePayload | null)?.errors;
    if (!Array.isArray(errors)) {
      return [];
    }

    return errors
      .map(error => this.normalizeImportError(error))
      .filter(error => !!error);
  }

  private normalizeImportError(error: unknown): ImportCatalogueError | undefined {
    if (typeof error === 'string') {
      return {message: error};
    }

    if (error && typeof error === 'object') {
      const item = error as { row?: unknown; column?: unknown; message?: unknown };
      return {
        row: typeof item.row === 'number' || typeof item.row === 'string' ? item.row : undefined,
        column: typeof item.column === 'string' ? item.column : undefined,
        message: typeof item.message === 'string' ? item.message : JSON.stringify(error)
      };
    }

    const message = JSON.stringify(error);
    return message ? {message} : undefined;
  }
}
